import React, { useEffect } from "react";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { analytics } from "MVVM/Analytics";

type TrialChoiceProps = {
  onChoose: (withCard: boolean) => void;
};

// Trial checkout chooser (ALL-1890): a FREE user starting the Unlimited trial
// picks between the carded 14-day trial and the no-card 7-day one before the
// embedded checkout mounts. The server enforces the same split from the
// trialWithCard flag; this step only sets it.
export const TrialChoice = ({ onChoose }: TrialChoiceProps) => {
  useEffect(() => {
    analytics.track("Trial Checkout Option Viewed", {});
  }, []);

  const choose = (withCard: boolean) => {
    analytics.track("Trial Checkout Option Selected", { withCard });
    onChoose(withCard);
  };

  return (
    <ChoiceContainer>
      <Title>Start your free Unlimited trial</Title>
      <Subtitle>Both are free. Pick how you want to start.</Subtitle>
      <Options>
        <OptionCard $featured onClick={() => choose(true)}>
          <FeaturedBadge>Double your trial</FeaturedBadge>
          <OptionDays>14 days free</OptionDays>
          <OptionDetail>
            Add a card now. You won&apos;t be charged until the trial ends, and
            you can cancel anytime.
          </OptionDetail>
          <OptionButton $featured>Get 14 days</OptionButton>
        </OptionCard>
        <OptionCard onClick={() => choose(false)}>
          <OptionDays>7 days free</OptionDays>
          <OptionDetail>
            No card needed. You can add one later to keep your trial going.
          </OptionDetail>
          <OptionButton>Get 7 days</OptionButton>
        </OptionCard>
      </Options>
    </ChoiceContainer>
  );
};

const ChoiceContainer = styled(Box)`
  background-color: ${({ theme }) => theme.colors.navy};
  border-radius: 12px;
  max-width: 720px;
  padding: 32px 28px;
  width: 90vw;
`;

const Title = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 700;
  text-align: center;
`;

const Subtitle = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  margin-top: 6px;
  text-align: center;
`;

const Options = styled(Flex)`
  gap: 16px;
  margin-top: 24px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    flex-direction: column;
  }
`;

const OptionCard = styled(Flex)<{ $featured?: boolean }>`
  background-color: ${({ theme }) => theme.colors.navy900};
  border: 1px solid
    ${({ $featured, theme }) =>
      $featured ? theme.colors.tierPlat : theme.colors.navy300};
  border-radius: 10px;
  cursor: pointer;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  position: relative;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ $featured, theme }) =>
      $featured ? theme.colors.ice : theme.colors.envy};
  }
`;

const FeaturedBadge = styled(Box)`
  background-color: ${({ theme }) => theme.colors.tierPlat};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.navy900};
  font-size: 11px;
  font-weight: 700;
  left: 20px;
  letter-spacing: 0.04em;
  padding: 3px 10px;
  position: absolute;
  text-transform: uppercase;
  top: -11px;
`;

const OptionDays = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 700;
`;

const OptionDetail = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  line-height: 1.45;
`;

const OptionButton = styled(Flex)<{ $featured?: boolean }>`
  align-items: center;
  background-color: ${({ $featured, theme }) =>
    $featured ? theme.colors.envy : "transparent"};
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  color: ${({ $featured, theme }) =>
    $featured ? theme.colors.navy900 : theme.colors.envy};
  font-size: 14px;
  font-weight: 600;
  justify-content: center;
  margin-top: auto;
  padding: 9px 0;
`;
